/**
 * Service Health Dashboard Component
 *
 * Main dashboard component that displays health status for all services
 * with auto-refresh functionality.
 */

import { useState, useEffect, useCallback } from 'react';
import logger from '../../../utils/logger';
import { getAllServiceEndpoints } from '../../../config/service-endpoints';
import { fetchAllServiceHealth, formatTimestamp } from '../../../utils/health-api';
import type { ServiceHealthStatus } from '../../../utils/health-api';
import { ServiceHealthCard } from '../ServiceHealthCard';
import { Surface } from '../../atoms/Surface';
import { Button } from '../../atoms/Button';
import { Heading } from '../../atoms/Heading';
import { Text } from '../../atoms/Text';
import { Spinner } from '../../atoms/Spinner';
import { Stack } from '../../layout/Stack';
import { StatusIndicator } from '../../atoms/StatusIndicator';

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

export function ServiceHealthDashboard() {
  const [services, setServices] = useState<ServiceHealthStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoints = getAllServiceEndpoints();
      const healthStatuses = await fetchAllServiceHealth(endpoints);

      setServices(healthStatuses);
      setLastRefresh(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch health status');
      logger.error('ServiceHealthDashboard', 'Error fetching health status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    if (!autoRefresh) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchHealth();
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [autoRefresh, fetchHealth]);

  return (
    <Stack spacing={6}>
      <Surface level="surface1" p={6}>
        <Stack spacing={4}>
          <Stack direction="horizontal" justify="between" align="center" className="flex-wrap gap-4">
            <Stack spacing={1}>
              <Heading level="h2">Service Health Status</Heading>
              <Text variant="caption">Monitor the health of all application services</Text>
            </Stack>

            <Stack direction="horizontal" spacing={4} align="center" className="flex-wrap">
              {lastRefresh && (
                <Text variant="caption">Last updated: {formatTimestamp(lastRefresh)}</Text>
              )}

              <Button
                variant={autoRefresh ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                aria-label={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
              >
                {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              </Button>

              <Button
                variant="primary"
                size="md"
                onClick={() => fetchHealth()}
                disabled={loading}
                loading={loading}
                aria-label="Manually refresh health status"
              >
                {loading ? 'Refreshing...' : 'Refresh Now'}
              </Button>
            </Stack>
          </Stack>

          {autoRefresh && (
            <Text variant="small">Automatically refreshing every 30 seconds</Text>
          )}

          {error && (
            <Surface level="surface2" p={4} className="bg-danger/10 border border-danger/20">
              <Text variant="caption" className="text-danger">
                <span className="font-medium">Error:</span> {error}
              </Text>
            </Surface>
          )}
        </Stack>
      </Surface>

      <Stack spacing={4}>
        {loading && services.length === 0 ? (
          <Surface level="surface1" p={12} className="text-center">
            <Stack spacing={4} align="center">
              <Spinner size="lg" className="text-primary" />
              <Text variant="muted">Loading health status...</Text>
            </Stack>
          </Surface>
        ) : services.length > 0 ? (
          services.map((service) => (
            <ServiceHealthCard key={service.serviceId} service={service} />
          ))
        ) : (
          <Surface level="surface1" p={12} className="text-center">
            <Text variant="muted">No services configured</Text>
          </Surface>
        )}
      </Stack>

      <Surface level="surface1" p={6}>
        <Stack spacing={3}>
          <Heading level="h3" className="text-sm">Status Legend</Heading>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stack direction="horizontal" spacing={2} align="center">
              <StatusIndicator status="pass" size="sm" />
              <Text variant="caption">Healthy - All checks passing</Text>
            </Stack>
            <Stack direction="horizontal" spacing={2} align="center">
              <StatusIndicator status="warn" size="sm" />
              <Text variant="caption">Degraded - Some warnings</Text>
            </Stack>
            <Stack direction="horizontal" spacing={2} align="center">
              <StatusIndicator status="fail" size="sm" />
              <Text variant="caption">Unhealthy - Checks failing</Text>
            </Stack>
            <Stack direction="horizontal" spacing={2} align="center">
              <StatusIndicator status="unknown" size="sm" />
              <Text variant="caption">Unknown - Cannot connect</Text>
            </Stack>
          </div>
        </Stack>
      </Surface>
    </Stack>
  );
}

export default ServiceHealthDashboard;
